import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { Briefcase, TrendingUp, Heart, Cpu, Utensils, ShoppingBag, Share2 } from 'lucide-react';

const Portfolio = () => {
  const { t } = useLanguage();

  // Sector-based cards - updated per user request
  const sectors = [
    { name: 'SaaS', icon: Cpu, description: 'Enterprise Software' },
    { name: 'Fintech', icon: TrendingUp, description: 'Financial Technology' },
    { name: 'Healthcare', icon: Heart, description: 'Health & Wellness' },
    { name: 'Food Tech', icon: Utensils, description: 'Food Innovation' },
    { name: 'E-Commerce', icon: ShoppingBag, description: 'Digital Commerce' },
    { name: 'Mobility', icon: Share2, description: 'Smart Infrastructure' },
  ];

  return (
    <section 
      data-testid="portfolio-section"
      className="py-16 md:py-24 bg-[#F8FAFB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#1A3A4A]/60 mb-4">
            {t('portfolio.subtitle')}
          </p>
          <h2 
            data-testid="portfolio-title"
            className="font-heading text-2xl md:text-3xl font-semibold text-[#1A3A4A]"
          >
            {t('portfolio.title')}
          </h2>
          <p className="font-body text-sm text-[#1A3A4A]/50 mt-3">
            {t('portfolio.sectorNote') || 'Sector-focused investments across growth stages'}
          </p>
        </motion.div>

        {/* Sector Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6"
        >
          {sectors.map((sector, index) => (
            <motion.div
              key={sector.name}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="group"
              data-testid={`portfolio-sector-${index}`}
            >
              <div className="bg-white rounded-xl p-5 md:p-6 flex flex-col items-center justify-center h-28 md:h-32 border border-[#1A3A4A]/5 shadow-sm hover:shadow-md hover:border-[#60B5AF]/30 transition-all duration-300">
                <sector.icon className="w-6 h-6 text-[#1A3A4A]/30 group-hover:text-[#60B5AF] transition-colors mb-2" />
                <span className="font-heading text-sm md:text-base font-semibold text-[#1A3A4A]/80 group-hover:text-[#1A3A4A] transition-colors text-center">
                  {sector.name}
                </span>
                <span className="font-body text-xs text-[#1A3A4A]/40 mt-0.5 text-center">
                  {sector.description}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Qualification Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center mt-10"
        >
          <p className="font-body text-sm text-[#1A3A4A]/60 italic">
            {t('portfolio.cta')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;
